import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { RecurringPeriod } from "@/modules/boxOffer/entities/boxOfferTypes"
import { cn } from "@/modules/lib/utils"
import { AlertIconDark } from "@images/svgIcons/alertIcon"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { deleteSubscriptionYupValidation } from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useDeleteBoxSubscription } from "@modules/boxSubscription/hooks/useBoxSubscription"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { formatDateWithHyphenUnbreakable } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { Info } from "lucide-react"
import { useImperativeHandle, useState } from "react"

export type BoxSubscriptionDeleteModalRef = {
  open: () => void
} & React.ComponentProps<"div">

export type BoxSubscriptionDeleteModalProps = {
  memberId: string
  subscription: BoxSubscriptionDetailsEntity | null
  setSubscriptionSelected: (subscription: BoxSubscriptionDetailsEntity | null) => void
  ref: React.Ref<BoxSubscriptionDeleteModalRef>
  className?: string
}

export function BoxSubscriptionDeleteModal({
  memberId,
  subscription,
  setSubscriptionSelected,
  ref,
  className,
  ...props
}: BoxSubscriptionDeleteModalProps) {
  const { deleteSubscription, isDeleteSubscriptionPending } = useDeleteBoxSubscription(memberId)
  const [showModal, setShowModal] = useState(false)
  const endDate = subscription?.periodEndDate
  const isRecurringPeriodMonthly = subscription?.recurringPeriod === RecurringPeriod.MONTHLY
  const recurringPeriodMs = dayjs.duration(subscription?.recurringPeriod ?? "P1M").asMilliseconds()

  const endDateOptions = [
    {
      value: "0",
      label: word("box.subscription.delete.modal.formik.currentPeriod") + " - " + dayjs(endDate).format("LL"),
    },
    {
      value: "1",
      label:
        word(
          isRecurringPeriodMonthly
            ? "box.subscription.delete.modal.formik.nextMonth"
            : "box.subscription.delete.modal.formik.nextPeriod",
        ) +
        " - " +
        dayjs(endDate).add(recurringPeriodMs, "millisecond").format("LL"),
    },
    {
      value: "2",
      label:
        word(
          isRecurringPeriodMonthly
            ? "box.subscription.delete.modal.formik.inMonths"
            : "box.subscription.delete.modal.formik.inPeriods",
          { count: 2 },
        ) +
        " - " +
        dayjs(endDate)
          .add(recurringPeriodMs * 2, "millisecond")
          .format("LL"),
    },
    {
      value: "3",
      label:
        word(
          isRecurringPeriodMonthly
            ? "box.subscription.delete.modal.formik.inMonths"
            : "box.subscription.delete.modal.formik.inPeriods",
          { count: 3 },
        ) +
        " - " +
        dayjs(endDate)
          .add(recurringPeriodMs * 3, "millisecond")
          .format("LL"),
    },
    {
      value: "4",
      label:
        word(
          isRecurringPeriodMonthly
            ? "box.subscription.delete.modal.formik.inMonths"
            : "box.subscription.delete.modal.formik.inPeriods",
          { count: 4 },
        ) +
        " - " +
        dayjs(endDate)
          .add(recurringPeriodMs * 4, "millisecond")
          .format("LL"),
    },
    {
      value: "5",
      label:
        word(
          isRecurringPeriodMonthly
            ? "box.subscription.delete.modal.formik.inMonths"
            : "box.subscription.delete.modal.formik.inPeriods",
          { count: 5 },
        ) +
        " - " +
        dayjs(endDate)
          .add(recurringPeriodMs * 5, "millisecond")
          .format("LL"),
    },
    {
      value: "6",
      label:
        word(
          isRecurringPeriodMonthly
            ? "box.subscription.delete.modal.formik.inMonths"
            : "box.subscription.delete.modal.formik.inPeriods",
          { count: 6 },
        ) +
        " - " +
        dayjs(endDate)
          .add(recurringPeriodMs * 6, "millisecond")
          .format("LL"),
    },
  ]

  const formik = useFormik({
    initialValues: {
      cancelAtPeriodEnd: false,
      endPeriodInMonths: undefined,
    },
    validationSchema: deleteSubscriptionYupValidation(),
    onSubmit: async () => {
      if (subscription) {
        await deleteSubscription({
          subscriptionId: subscription.id,
          cancelAtPeriodEnd: formik.values.cancelAtPeriodEnd,
          endPeriodInMonths: formik.values.endPeriodInMonths,
        })
          .then(() => {
            toast.success(word("box.subscription.delete.modal.toast.success"), {
              classNames: successToastClassName(),
            })
            onHide()
          })
          .catch(() => {
            toast.error(word("box.subscription.delete.modal.toast.error"), {
              classNames: errorToastClassName(),
            })
          })
      }
    },
  })

  const onHide = () => {
    formik.resetForm()
    setSubscriptionSelected(null)
    setShowModal(false)
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className={cn("flex w-[480px] flex-col gap-8", className)} aria-describedby={undefined} {...props}>
        <DialogHeader>
          <DialogTitle>
            <div className="flex flex-col flex-wrap content-center justify-center gap-2 pt-6 text-center">
              <div className="flex w-full content-center justify-center">
                <AlertIconDark color="currentColor" className="text-textError" />
              </div>
              <Text fontStyle="TL" className="text-textDefault-body">
                {word("box.subscription.delete.modal.header.title")}
              </Text>
            </div>
          </DialogTitle>
        </DialogHeader>

        {subscription?.minimumCommitmentDate ? (
          <HUCardAlert
            title={`${word("box.subscription.delete.modal.alert.title")} ${formatDateWithHyphenUnbreakable(dayjs(subscription.minimumCommitmentDate).format("L"))}`}
            icon={<Info size={45} className="mx-2" />}
            className="flex flex-row items-center rounded-2xl py-2"
            titleFontStyle="BL"
            style={{ whiteSpace: "break-spaces" }}
          />
        ) : null}

        {endDate && (
          <>
            <Text fontStyle="BM" className="flex content-center justify-center text-center text-textDefault-caption">
              {word("box.subscription.delete.modal.header.subtitle")}
            </Text>

            {/* CONTENT */}
            <div className="flex w-full gap-4">
              <div
                className={`flex w-1/2 cursor-pointer flex-col justify-center gap-3 rounded-2xl border p-3 ${formik.getFieldProps("cancelAtPeriodEnd").value ? "border-borderDefault" : "border-borderPrimary"}`}
                onClick={() => {
                  formik.setFieldValue("cancelAtPeriodEnd", false)
                  formik.setFieldValue("endPeriodInMonths", undefined)
                }}
              >
                <Text fontStyle="TS" className="text-center text-textDefault-body">
                  {subscription?.outOfBand
                    ? word("box.subscription.delete.modal.content.outOfBandNow")
                    : word("box.subscription.delete.modal.content.now")}
                </Text>
              </div>
              <div
                className={`flex w-1/2 cursor-pointer flex-col justify-center gap-3 rounded-2xl border p-3 ${formik.getFieldProps("cancelAtPeriodEnd").value ? "border-borderPrimary" : "border-borderDefault"}`}
                onClick={() => void formik.setFieldValue("cancelAtPeriodEnd", true)}
              >
                <Text fontStyle="TS" className="text-center text-textDefault-body">
                  {word("box.subscription.delete.modal.content.endDate")}
                </Text>
              </div>
            </div>

            <DropdownInput
              options={endDateOptions.filter(Boolean) as { label: string; value: string; disabled?: boolean }[]}
              error={getFormErrorMessage("endPeriodInMonths", formik)}
            >
              <Select
                disabled={formik.getFieldProps("cancelAtPeriodEnd").value ? false : true}
                defaultValue={String(formik.values.endPeriodInMonths ?? "undefined")}
                onValueChange={(value) => {
                  if (!value || value === "undefined") {
                    formik.setFieldValue("endPeriodInMonths", undefined)
                  } else {
                    formik.setFieldValue("endPeriodInMonths", Number(value))
                  }
                }}
                items={endDateOptions.filter(Boolean) as { label: string; value: string; disabled?: boolean }[]}
              >
                <SelectTrigger id={"endPeriodInMonths"}>
                  <SelectValue placeholder={word("box.subscription.delete.modal.formik.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {endDateOptions.filter(Boolean).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DropdownInput>
          </>
        )}
        {/* BUTTON */}
        <div className="mb-4 flex w-full flex-row items-center justify-center gap-2">
          <Button variant="outline" size="lg" onClick={onHide} className="grow">
            {word("global.cancel")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={() => void formik.submitForm()}
            className="grow"
            disabled={isDeleteSubscriptionPending}
          >
            {word("global.remove.standard")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
